import { authService } from "../../../utils/authService";

export type AnalyticsSession = {
  session_id: string;
  session_name: string;
  track_id?: string | null;
  track_name?: string | null;
  course_id?: string | null;
  course_name?: string | null;
  batch_id?: string | null;
  batch_name?: string | null;
  branch?: string | null;
  planned_minutes?: number | null;
};

export type AnalyticsSubtopic = {
  sn: number;
  id: string;
  title: string;
  parent_id?: string | null;
  planned_minutes: number | null;
  actual_minutes: number | null;
  distraction_percent: number | null;
  quiz_pre_percent: number | null;
  quiz_post_percent: number | null;
  quiz_pre_max: number | null;
  quiz_post_max: number | null;
  grasp_understood_percent: number | null;
  grasp_partial_percent: number | null;
  grasp_did_not_percent: number | null;
  qa_count: number | null;
};

export type StudentQuizSlot = { score: number; max: number };

export type AttendanceLog = {
  session_start_time?: string | null;
  session_end_time?: string | null;
  duration_minutes?: number | null;
};

export type AnalyticsStudent = {
  sn: number;
  student_id: string;
  student_name: string;
  branch?: string | null;
  attendance_minutes: number | null;
  attendance_joins: number | null;
  attendance_label: string;
  attendance_segments: number[];
  attendance_logs?: AttendanceLog[];
  pre_quiz_score: number | null;
  pre_quiz_max: number | null;
  post_quiz_score: number | null;
  post_quiz_max: number | null;
  distraction_score: number | null;
  grasp_understood_percent: number | null;
  grasp_partial_percent: number | null;
  grasp_did_not_percent: number | null;
  attitude: string[];
  comment: string | null;
  rating_percent: number | null;
  quiz_by_subtopic?: Record<string, { pre?: StudentQuizSlot; post?: StudentQuizSlot }>;
  grasp_by_subtopic?: Record<
    string,
    { understood: number | null; partial: number | null; did_not: number | null }
  >;
  distraction_by_subtopic?: Record<string, number | null>;
  engagement_by_subtopic?: Record<
    string,
    {
      focused_seconds?: number;
      blurred_seconds?: number;
      hidden_seconds?: number;
      fullscreen_seconds?: number;
      mini_seconds?: number;
      mini_events?: number;
      tab_switches?: number;
      alert_popup_seconds?: number;
      alert_popup_events?: number;
    }
  >;
};

export type GraspFeedbackRow = {
  student_id: string;
  student_name: string;
  topic_id: string;
  topic_title: string;
  level: "partial" | "not_understood" | "understood";
  level_label: string;
  message: string;
  confusion_tags: string[];
  unclear_subtopics: string[];
  question_comment: string;
  trainer_answer: string;
  status: "open" | "closed" | string;
  closed_at: number;
  submitted_at: number;
};

export type SessionAnalyticsResponse = {
  success: boolean;
  session: AnalyticsSession;
  subtopics: AnalyticsSubtopic[];
  students: AnalyticsStudent[];
  attitude_options: string[];
  grasp_feedbacks: GraspFeedbackRow[];
};

function asNum(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(value.map((x) => String(x || "").trim()).filter(Boolean)),
  );
}

function graspLevelLabel(level: string, fallback?: string): string {
  const trimmed = String(fallback || "").trim();
  if (trimmed) return trimmed;
  const key = String(level || "").trim().toLowerCase();
  if (key === "partial") return "Partial";
  if (key === "understood") return "Understood";
  if (key === "not_understood") return "Did Not";
  return key || "—";
}

function normalizeGraspFeedback(raw: any): GraspFeedbackRow | null {
  if (!raw || typeof raw !== "object") return null;
  const level = String(raw.level || "").trim().toLowerCase();
  if (level !== "partial" && level !== "not_understood" && level !== "understood") {
    return null;
  }
  let message = String(raw.message || "").trim();
  let confusion_tags = asStringList(raw.confusion_tags);
  let unclear_subtopics = asStringList(raw.unclear_subtopics);
  const legacyComment = String(raw.question_comment || "").trim();
  // Stale analytics cache may only have the joined question_comment string.
  if (!message && !confusion_tags.length && !unclear_subtopics.length && legacyComment) {
    message = legacyComment;
  }
  const question_comment =
    legacyComment ||
    [message, ...confusion_tags, ...unclear_subtopics].filter(Boolean).join(" | ");
  const submitted_at = Number(raw.submitted_at || 0) || 0;
  const status =
    level === "understood"
      ? "closed"
      : String(raw.status || "open").toLowerCase() || "open";
  const closed_at =
    level === "understood"
      ? Number(raw.closed_at || raw.submitted_at || 0) || 0
      : Number(raw.closed_at || 0) || 0;
  return {
    student_id: String(raw.student_id || ""),
    student_name: String(raw.student_name || raw.student_id || "Student"),
    topic_id: String(raw.topic_id || ""),
    topic_title: String(raw.topic_title || ""),
    level,
    level_label: graspLevelLabel(level, raw.level_label),
    message,
    confusion_tags,
    unclear_subtopics,
    question_comment,
    trainer_answer: String(raw.trainer_answer || ""),
    status,
    closed_at,
    submitted_at,
  };
}

export function graspVotesToFeedbackRows(votes: any[]): GraspFeedbackRow[] {
  const latest = new Map<string, any>();
  for (const vote of votes || []) {
    if (!vote || typeof vote !== "object") continue;
    const level = String(vote.level || "").trim().toLowerCase();
    if (level !== "partial" && level !== "not_understood" && level !== "understood") {
      continue;
    }
    const stid = String(vote.student_id || vote.studentId || "").trim();
    const tid = String(vote.topic_id || vote.topicId || "").trim();
    if (!stid || !tid) continue;
    const key = `${stid}::${tid}`;
    const prev = latest.get(key);
    const submitted = Number(vote.submitted_at ?? vote.submittedAt ?? 0) || 0;
    if (!prev || submitted >= (Number(prev.submitted_at ?? prev.submittedAt ?? 0) || 0)) {
      latest.set(key, vote);
    }
  }
  return Array.from(latest.values())
    .map((vote) =>
      normalizeGraspFeedback({
        student_id: vote.student_id ?? vote.studentId,
        student_name: vote.student_name ?? vote.studentName,
        topic_id: vote.topic_id ?? vote.topicId,
        topic_title: vote.topic_title ?? vote.topicTitle,
        level: vote.level,
        level_label: graspLevelLabel(String(vote.level || "")),
        message: vote.message,
        confusion_tags: vote.confusion_tags ?? vote.confusionTags,
        unclear_subtopics: vote.unclear_subtopics ?? vote.unclearSubtopics,
        trainer_answer: vote.trainer_answer ?? vote.trainerAnswer,
        status: vote.status,
        closed_at: vote.closed_at ?? vote.closedAt,
        submitted_at: vote.submitted_at ?? vote.submittedAt,
      }),
    )
    .filter((row: GraspFeedbackRow | null): row is GraspFeedbackRow => Boolean(row))
    .sort((a, b) => {
      const aClosed = a.status === "closed" ? 1 : 0;
      const bClosed = b.status === "closed" ? 1 : 0;
      if (aClosed !== bClosed) return aClosed - bClosed;
      return (b.submitted_at || 0) - (a.submitted_at || 0);
    });
}

const GRASP_FEEDBACK_CACHE_TTL_MS = 20_000;
const graspFeedbackCache = new Map<
  string,
  { at: number; rows: GraspFeedbackRow[]; inflight?: Promise<GraspFeedbackRow[]> }
>();

export async function fetchGraspFeedbackRows(
  sessionId: string,
): Promise<GraspFeedbackRow[]> {
  const sid = String(sessionId || "").trim();
  if (!sid) return [];
  const hit = graspFeedbackCache.get(sid);
  const now = Date.now();
  if (hit && now - hit.at < GRASP_FEEDBACK_CACHE_TTL_MS) {
    return hit.rows;
  }
  if (hit?.inflight) {
    return hit.inflight;
  }
  const inflight = (async () => {
    const res = await authService.get(
      `/session/sessions/${encodeURIComponent(sid)}/grasp/`,
    );
    const rows = graspVotesToFeedbackRows(res?.data?.votes || []);
    graspFeedbackCache.set(sid, { at: Date.now(), rows });
    return rows;
  })();
  graspFeedbackCache.set(sid, { at: 0, rows: [], inflight });
  try {
    return await inflight;
  } catch (err) {
    const cur = graspFeedbackCache.get(sid);
    if (cur?.inflight === inflight) {
      graspFeedbackCache.delete(sid);
    }
    throw err;
  }
}

export function invalidateGraspFeedbackCache(sessionId?: string): void {
  if (!sessionId) {
    graspFeedbackCache.clear();
    return;
  }
  graspFeedbackCache.delete(String(sessionId).trim());
}

export async function fetchSessionAnalyticsReport(
  sessionId: string,
): Promise<SessionAnalyticsResponse> {
  const res = await authService.get(
    `/session/sessions/${encodeURIComponent(sessionId)}/analytics-report/`,
  );
  const data = res?.data || {};
  return {
    success: Boolean(data.success),
    session: {
      session_id: String(data.session?.session_id ?? sessionId),
      session_name: String(data.session?.session_name ?? ""),
      track_id: data.session?.track_id ?? null,
      track_name: data.session?.track_name ?? null,
      course_id: data.session?.course_id ?? null,
      course_name: data.session?.course_name ?? null,
      batch_id: data.session?.batch_id ?? null,
      batch_name: data.session?.batch_name ?? null,
      branch: data.session?.branch ?? null,
      planned_minutes: asNum(data.session?.planned_minutes),
    },
    subtopics: Array.isArray(data.subtopics) ? data.subtopics : [],
    students: Array.isArray(data.students)
      ? data.students.map((row: any) => ({
          ...row,
          attendance_logs: Array.isArray(row?.attendance_logs)
            ? row.attendance_logs.map((log: any) => ({
                session_start_time:
                  log?.session_start_time ?? log?.logged_in ?? null,
                session_end_time:
                  log?.session_end_time ?? log?.logged_out ?? null,
                duration_minutes:
                  log?.duration_minutes != null
                    ? Number(log.duration_minutes)
                    : null,
              }))
            : [],
        }))
      : [],
    attitude_options: Array.isArray(data.attitude_options)
      ? data.attitude_options
      : [],
    grasp_feedbacks: Array.isArray(data.grasp_feedbacks)
      ? (data.grasp_feedbacks as unknown[])
          .map((raw) => normalizeGraspFeedback(raw))
          .filter((row: GraspFeedbackRow | null): row is GraspFeedbackRow => row != null)
      : [],
  };
}

export async function respondGraspFeedback(
  sessionId: string,
  payload: {
    student_id: string;
    topic_id: string;
    trainer_answer?: string;
    close?: boolean;
  },
): Promise<GraspFeedbackRow | null> {
  const res = await authService.patch(
    `/session/sessions/${encodeURIComponent(sessionId)}/grasp/respond/`,
    payload,
  );
  const vote = res?.data?.vote;
  if (!vote) return null;
  invalidateGraspFeedbackCache(sessionId);
  return normalizeGraspFeedback({
    ...vote,
    level_label: graspLevelLabel(String(vote.level || ""), vote.level_label),
  });
}
